const FRESHNESS_STATES = new Set([
  'initial_sync',
  'unknown',
  'fresh',
  'recent',
  'refreshing',
  'stale',
  'provider_error',
  'requires_action',
]);

const isIgnoredAccount = (account = {}) => (
  account.isIgnored === true || account.is_ignored === true
);

const getKnownBalance = (account = {}) => (
  account.balance
  ?? account.currentBalance
  ?? account.current_balance
  ?? null
);

const getExplicitPersistedBalance = (account = {}) => {
  const value = account.hasPersistedBalance ?? account.has_persisted_balance;
  if (value === true || value === 1 || value === '1' || value === 'true') return true;
  if (value === false || value === 0 || value === '0' || value === 'false') return false;
  return null;
};

export const hasKnownBalance = (account = {}) => {
  const hasPersistedBalance = getExplicitPersistedBalance(account);
  if (hasPersistedBalance !== null) return hasPersistedBalance;

  const balance = getKnownBalance(account);
  return balance !== null
    && balance !== undefined
    && balance !== ''
    && Number.isFinite(Number(balance));
};

const normalizeState = (value, account = {}) => {
  const state = String(value || '').trim().toLowerCase();
  if (FRESHNESS_STATES.has(state)) return state;
  const updatedAt = account.balanceUpdatedAt || account.balance_updated_at || null;
  return hasKnownBalance(account) || updatedAt ? 'recent' : 'initial_sync';
};

export const normalizeAccountBalanceFreshness = (account = {}) => {
  const raw = account.balanceFreshness ?? account.balance_freshness;
  const object = raw && typeof raw === 'object' ? raw : {};
  return {
    state: normalizeState(object.state || raw, account),
    ageSeconds: Number.isFinite(Number(object.ageSeconds ?? object.age_seconds))
      ? Number(object.ageSeconds ?? object.age_seconds)
      : null,
    updatedAt: object.updatedAt
      || object.updated_at
      || account.balanceUpdatedAt
      || account.balance_updated_at
      || null,
  };
};

export const normalizeNetWorthSummaryFreshness = (summary) => {
  if (!summary || typeof summary !== 'object') return summary || null;
  const accounts = Array.isArray(summary.accounts)
    ? summary.accounts.map((account) => {
        const hasPersistedBalance = getExplicitPersistedBalance(account);
        return {
          ...account,
          ...(hasPersistedBalance !== null ? { hasPersistedBalance } : {}),
          balanceFreshness: normalizeAccountBalanceFreshness(account),
        };
      })
    : [];
  const raw = summary.balanceFreshness && typeof summary.balanceFreshness === 'object'
    ? summary.balanceFreshness
    : {};
  const count = (name, state) => {
    const explicit = Number(raw[name]);
    return accounts.length === 0 && Number.isFinite(explicit)
      ? Math.max(0, explicit)
      : accounts.filter((account) => (
          !isIgnoredAccount(account) && account.balanceFreshness.state === state
        )).length;
  };
  const initialSync = count('initialSync', 'initial_sync');
  const unknown = count('unknown', 'unknown');
  const fresh = count('fresh', 'fresh');
  const recent = count('recent', 'recent');
  const refreshing = count('refreshing', 'refreshing');
  const stale = count('stale', 'stale');
  const providerError = count('providerError', 'provider_error');
  const hasIncompleteInitialSync = accounts.length > 0
    ? initialSync > 0
    : raw.hasIncompleteInitialSync === true || initialSync > 0;
  const hasUsableCachedBalances = raw.hasUsableCachedBalances === true
    || accounts.some((account) => !isIgnoredAccount(account) && hasKnownBalance(account));

  return {
    ...summary,
    accounts,
    balanceFreshness: {
      initialSync,
      unknown,
      fresh,
      recent,
      refreshing,
      stale,
      providerError,
      hasIncompleteInitialSync,
      hasUsableCachedBalances,
    },
  };
};

export const getNetWorthFreshnessPresentation = (summary = {}) => {
  const freshness = summary?.balanceFreshness || {};
  if (freshness.hasIncompleteInitialSync && !freshness.hasUsableCachedBalances) {
    return {
      kind: 'initial_sync',
      prominent: true,
      message: 'Finishing your first balance update…',
    };
  }
  if (Number(freshness.refreshing) > 0) {
    return {
      kind: 'refreshing',
      prominent: false,
      message: 'Updating balances in the background',
    };
  }
  return null;
};
